import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Play, Loader2, RefreshCw } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { costingService } from '../../services/costingService';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import { periodLabel } from '../../utils/financialPeriods';

// =============================================================================
// Costing run page — shared by Procurement > Costing and Manufacturing > Costing.
//
// Both pages are the same shape: press Run, then read the answer on one tab and
// what the run glossed over on the other. Only the proc it calls, the columns
// it shows and the wording differ, so they are one component with a config
// object rather than two near-identical files.
//
// The Errors tab matters more than it looks. The calc procs never fail on bad
// data — a missing price contributes zero, a missing batch size drops a setup —
// so the default failure mode is a cost that is quietly too low. The exceptions
// are how that becomes visible. An ERROR means the number is wrong; a WARN
// means it is a guess.
// =============================================================================

const money = (n) => (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money4 = (n) => (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const int0 = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const T_CALC = { bgCell: 'rgba(100, 116, 139, 0.10)' };
const T_RESULT = { bgCell: 'rgba(16, 185, 129, 0.12)', textDark: '#047857', baseFontStyle: '600 12px' };
const T_MUTED = { textDark: '#94a3b8' };
const T_WARNCELL = { bgCell: 'rgba(245, 158, 11, 0.12)', textDark: '#b45309' };
const B_ERROR = { bgBubble: 'rgba(239, 68, 68, 0.18)', textBubble: '#b91c1c' };
const B_WARN = { bgBubble: 'rgba(245, 158, 11, 0.20)', textBubble: '#b45309' };
const B_INFO = { bgBubble: 'rgba(100, 116, 139, 0.16)', textBubble: '#475569' };

const EXC_COLUMNS = [
  { id: 'severity', title: 'Severity', width: 90 },
  { id: 'code',     title: 'Code',     width: 210 },
  { id: 'entity',   title: 'Entity',   width: 280, grow: 1 },
  { id: 'period',   title: 'Period',   width: 90 },
  { id: 'detail',   title: 'What it means', width: 560, grow: 2 },
];
const EXC_IDS = EXC_COLUMNS.map((c) => c.id);

const SEV_RANK = { ERROR: 0, WARN: 1, INFO: 2 };

const CostingRunPage = ({ config }) => {
  const { selectedVersion, selectedSite, fyStartMonth } = useAppContext();
  const [tab, setTab] = useState('results');
  const [rows, setRows] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [run, setRun] = useState(null);   // stored run metadata, null = never run
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const ready = !!selectedVersion;

  const load = useCallback(async () => {
    if (!ready) { setRows([]); setExceptions([]); return; }
    setLoading(true);
    setError('');
    try {
      const res = await config.fetch();
      if (res?.success) {
        setRows(res.data.rows || []);
        // Worst first — an ERROR buried under 200 INFO rows may as well not be
        // reported at all.
        setExceptions([...(res.data.exceptions || [])].sort(
          (a, b) => (SEV_RANK[a.severity] ?? 9) - (SEV_RANK[b.severity] ?? 9)
        ));
        setRun(res.data.run || null);
      } else {
        setError(res?.message || 'Could not load the results.');
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load the results.');
    } finally {
      setLoading(false);
    }
  }, [ready, config]);

  useEffect(() => { load(); }, [load]);

  const doRun = async () => {
    setRunning(true);
    setError('');
    try {
      const res = await config.run();
      if (res?.success) {
        await load();
      } else {
        // A proc that throws (missing table, bad data) lands here rather than
        // leaving a dead spinner.
        setError(res?.message || 'The calculation failed.');
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'The calculation failed.');
    } finally {
      setRunning(false);
    }
  };

  const counts = useMemo(() => ({
    error: run?.error_count ?? exceptions.filter((e) => e.severity === 'ERROR').length,
    warn: run?.warn_count ?? exceptions.filter((e) => e.severity === 'WARN').length,
  }), [run, exceptions]);

  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const excRef = useRef(exceptions);
  excRef.current = exceptions;

  const getResultCell = useCallback(
    ([col, row]) => config.getCell(rowsRef.current[row], config.columnIds[col], fyStartMonth),
    [config, fyStartMonth]
  );

  const getExcCell = useCallback(([col, row]) => {
    const e = excRef.current[row];
    const id = EXC_IDS[col];
    if (!e) return gcell.text('', { readonly: true });
    switch (id) {
      case 'severity':
        return gcell.bubble([e.severity], {
          theme: e.severity === 'ERROR' ? B_ERROR : e.severity === 'WARN' ? B_WARN : B_INFO,
        });
      case 'code': return gcell.text(e.code || '', { readonly: true });
      case 'entity': return gcell.text(e.entity || '', { readonly: true });
      case 'period':
        return e.period == null
          ? gcell.text('—', { readonly: true, theme: T_MUTED })
          : gcell.text(periodLabel(e.period, fyStartMonth), { readonly: true, theme: T_MUTED });
      case 'detail': return gcell.text(e.detail || '', { readonly: true, theme: T_MUTED });
      default: return gcell.text('', { readonly: true });
    }
  }, [fyStartMonth]);

  const tabClass = (id) =>
    `px-3 py-1 text-xs font-semibold rounded-md border transition-colors ${
      tab === id
        ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200'
        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
    }`;

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-4 pb-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <h1 className="m-0 mr-1 text-lg font-bold leading-7 tracking-tight text-slate-900 dark:text-slate-100">
            {config.title}
          </h1>

          <div className="flex items-center gap-1.5">
            <button type="button" className={tabClass('results')} onClick={() => setTab('results')}>
              Results{rows.length > 0 ? ` (${int0(rows.length)})` : ''}
            </button>
            <button type="button" className={tabClass('errors')} onClick={() => setTab('errors')}>
              Errors{counts.error > 0 ? ` (${counts.error})` : counts.warn > 0 ? ` (${counts.warn} warn)` : ''}
            </button>
          </div>

          {counts.error > 0 && (
            <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold border border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
              {counts.error} error{counts.error === 1 ? '' : 's'} — the cost is wrong
            </span>
          )}
          {counts.error === 0 && counts.warn > 0 && (
            <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold border border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
              {counts.warn} warning{counts.warn === 1 ? '' : 's'}
            </span>
          )}
          {error && <span className="text-[11px] text-red-600 dark:text-red-400">{error}</span>}

          <div className="ml-auto flex items-center gap-2">
            <span className="text-[11px] text-slate-400">
              {run
                ? `Last run ${new Date(run.run_at).toLocaleString()}${run.duration_ms ? ` · ${(run.duration_ms / 1000).toFixed(1)}s` : ''}${run.site_code ? ` · ${run.site_code}` : ' · all sites'}`
                : 'Never run'}
            </span>
            <button
              type="button" onClick={load} disabled={!ready || loading || running}
              title="Reload without recalculating"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button
              type="button" onClick={doRun} disabled={!ready || running}
              className={`inline-flex items-center gap-1.5 px-3 py-1 text-[11px] font-semibold rounded-md text-white shadow-sm transition-colors ${
                ready && !running ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 dark:bg-slate-600'
              }`}
            >
              {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
              {running ? 'Calculating…' : config.runLabel}
            </button>
          </div>
        </div>

        <p className="m-0 mt-2 text-[11px] text-slate-500 dark:text-slate-400">
          {tab === 'results' ? config.blurb : 'What the run could not do properly, as recorded AT THE TIME OF THE RUN — so it always describes the figures on the Results tab, not the data as it stands now. ERROR = the number is wrong. WARN = the number is a guess.'}
          {selectedSite ? '' : ' · All sites'}
        </p>

        {run?.steps?.length > 0 && (
          <p className="m-0 mt-1 text-[11px] text-emerald-700 dark:text-emerald-400">
            {run.steps.map((s) => `${s.step}: ${int0(s.rows_written)} rows`).join('  ·  ')}
          </p>
        )}
      </div>

      <div className="flex-1 min-h-0 px-6 pb-4">
        <div className="h-full flex flex-col rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
          {!ready ? (
            <div className="p-10 text-center text-sm text-slate-400">
              Select a budget version in the top bar to begin.
            </div>
          ) : loading ? (
            <div className="p-10 text-center text-sm text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
            </div>
          ) : tab === 'results' ? (
            rows.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-400">
                Nothing calculated yet — press <span className="font-semibold">{config.runLabel}</span>.
              </div>
            ) : (
              <MantleDataGrid
                gridId={config.gridId}
                columns={config.columns}
                rowCount={rows.length}
                getCellContent={getResultCell}
                freezeColumns={2}
                hint={config.hint}
              />
            )
          ) : exceptions.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400">
              {run
                ? 'No exceptions — every input this run needed was present.'
                : <>Never run.<br /><span className="text-xs">Exceptions are recorded when the calculation runs.</span></>}
            </div>
          ) : (
            <MantleDataGrid
              gridId={`${config.gridId}-exc`}
              columns={EXC_COLUMNS}
              rowCount={exceptions.length}
              getCellContent={getExcCell}
              freezeColumns={1}
              hint="Sorted worst first. ERROR means a figure is wrong; WARN means it rests on a fallback."
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ---- Procurement: prices ----------------------------------------------------
const PRICE_COLUMNS = [
  { id: 'warehouse_id', title: 'Warehouse', width: 130 },
  { id: 'stock_code',   title: 'Item',      width: 300, grow: 1 },
  { id: 'financial_period', title: 'Period', width: 90 },
  { id: 'price_actual',  title: 'Price (as captured)', width: 160 },
  { id: 'price_applied', title: 'Price (applied)',     width: 150 },
  { id: 'required_qty',  title: 'Qty consumed',        width: 140 },
  { id: 'pricing_method', title: 'Method',             width: 150 },
];

export const procurementCostingConfig = {
  title: 'Procurement Costing',
  runLabel: 'Run pricing',
  gridId: 'costing-pricing',
  columns: PRICE_COLUMNS,
  columnIds: PRICE_COLUMNS.map((c) => c.id),
  blurb: 'Landed procurement price per item, warehouse and month. "As captured" is the raw landed build-up; "applied" is after the Purchase > Budget Setup pricing method. Running this also re-imploses the BOMs, because the weighted average is weighted by what they consume.',
  hint: 'Where the two price columns differ, the pricing method changed it — weighted average collapses the year to one price that every month takes on.',
  fetch: () => costingService.getPricing(),
  run: () => costingService.runPricing(),
  getCell: (r, id, fyStartMonth) => {
    if (!r) return gcell.text('', { readonly: true });
    switch (id) {
      case 'warehouse_id': return gcell.text(r.warehouse_id, { readonly: true });
      case 'stock_code': return gcell.text(`${r.stock_code} · ${r.description}`, { readonly: true });
      case 'financial_period': return gcell.text(periodLabel(r.financial_period, fyStartMonth), { readonly: true, theme: T_MUTED });
      case 'price_actual': return gcell.number(r.price_actual, { readonly: true, display: money4(r.price_actual), theme: T_CALC });
      case 'price_applied': return gcell.number(r.price_applied, { readonly: true, display: money4(r.price_applied), theme: T_RESULT });
      case 'required_qty': return gcell.number(r.required_qty, { readonly: true, display: int0(r.required_qty), theme: T_CALC });
      case 'pricing_method': return gcell.text(r.pricing_method || '', { readonly: true, theme: T_MUTED });
      default: return gcell.text('', { readonly: true });
    }
  },
};

// ---- Manufacturing: finished-good cost --------------------------------------
const FG_COLUMNS = [
  { id: 'site_code',   title: 'Site',   width: 100 },
  { id: 'parent_code', title: 'Item',   width: 300, grow: 1 },
  { id: 'item_type',   title: 'Type',   width: 130 },
  { id: 'financial_period', title: 'Period', width: 90 },
  { id: 'volume',        title: 'Volume',        width: 120 },
  { id: 'material_cost', title: 'Material',      width: 130 },
  { id: 'setup_cost',    title: 'Setup',         width: 120 },
  { id: 'run_cost',      title: 'Run',           width: 120 },
  { id: 'total_unit_cost', title: 'Cost / Unit', width: 140 },
  { id: 'unpriced_components', title: 'Unpriced', width: 100 },
];

export const manufacturingCostingConfig = {
  title: 'Manufacturing Costing',
  runLabel: 'Run full costing',
  gridId: 'costing-fg',
  columns: FG_COLUMNS,
  columnIds: FG_COLUMNS.map((c) => c.id),
  blurb: 'The finished cost of one unit, by month: imploded material at procurement prices, plus work-centre setup and run time at their rates. Runs the whole chain — implosion, prices, then this.',
  hint: 'Cost / Unit = material + setup + run. A non-zero Unpriced count means components had no price and the cost is understated by whatever they are worth.',
  fetch: () => costingService.getFgCost(),
  run: () => costingService.runFull(),
  getCell: (r, id, fyStartMonth) => {
    if (!r) return gcell.text('', { readonly: true });
    switch (id) {
      case 'site_code': return gcell.text(r.site_code, { readonly: true });
      case 'parent_code': return gcell.text(`${r.parent_code} · ${r.description}`, { readonly: true });
      case 'item_type': return gcell.text(r.item_type || '—', { readonly: true, theme: T_MUTED });
      case 'financial_period': return gcell.text(periodLabel(r.financial_period, fyStartMonth), { readonly: true, theme: T_MUTED });
      case 'volume': return gcell.number(r.volume, { readonly: true, display: int0(r.volume), theme: T_CALC });
      case 'material_cost': return gcell.number(r.material_cost, { readonly: true, display: money4(r.material_cost), theme: T_CALC });
      case 'setup_cost': return gcell.number(r.setup_cost, { readonly: true, display: money4(r.setup_cost), theme: T_CALC });
      case 'run_cost': return gcell.number(r.run_cost, { readonly: true, display: money4(r.run_cost), theme: T_CALC });
      case 'total_unit_cost': return gcell.number(r.total_unit_cost, { readonly: true, display: money(r.total_unit_cost), theme: T_RESULT });
      case 'unpriced_components':
        return r.unpriced_components > 0
          ? gcell.number(r.unpriced_components, { readonly: true, display: int0(r.unpriced_components), theme: T_WARNCELL })
          : gcell.text('—', { readonly: true, theme: T_MUTED });
      default: return gcell.text('', { readonly: true });
    }
  },
};

export default CostingRunPage;
